import { useTheme } from '@mui/system';
import { DEV_REV_DISABLED_ROUTES } from 'constants/routes';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { IChatBubbleContext, IDevRevPLuG } from 'types/devRev';
import { ITheme, ThemeMode } from 'types/theme';
import DevRevButton from './DevRevButton';
import ChatBubbleContext from './SupportBubbleContext';

const HORIZONTAL_OFFSET = 20;
const BOTTOM_OFFSET = 75;

interface ICustomWindow extends Window {
	Plug?: any;
}

declare const window: ICustomWindow;

interface IDevRevWidgetProps {
	sessionToken: string;
	isLoading: boolean;
}

let widgetRef: IDevRevPLuG;

const DevRevWidget: React.FC<IDevRevWidgetProps> = ({
	sessionToken,
	isLoading
}) => {
	const {
		palette: { mode }
	} = useTheme<ITheme>();
	const [widget, setWidget] = React.useState<IDevRevPLuG | null>(null);
	const [isOpen, setIsOpen] = React.useState(false);
	const { pathname } = useLocation();
	const { shouldOpen, close } =
		React.useContext<IChatBubbleContext>(ChatBubbleContext);

	const config = useMemo(
		() =>
			!isLoading
				? {
						auth: {
							sessionToken
						},
						style: {
							alignment: 'left',
							hideFloatingToggleButton: true,
							horizontalOffset: `${HORIZONTAL_OFFSET}px`,
							verticalOffset: `${BOTTOM_OFFSET}px`,
							theme: mode === ThemeMode.dark ? ThemeMode.light : ThemeMode.dark
						}
				  }
				: undefined,
		[isLoading, mode, sessionToken]
	);

	const widgetOnReady = React.useCallback(
		(widgetReady: IDevRevPLuG, open: boolean) => () => {
			if (open) {
				widgetReady.open();
				setIsOpen(true);
			}
			widgetRef = widgetReady;
		},
		[]
	);

	const widgetOnClose = React.useCallback(() => {
		setIsOpen(false);
	}, []);

	useEffect(() => {
		if (config) {
			try {
				if (widget) {
					widget.reInitialize(config);
					widget.onReady(widgetOnReady(widget, isOpen));
					widget.onClose(widgetOnClose);
				} else {
					const newWidget = new window.Plug(config);
					setWidget(newWidget);

					// open for the first time
					newWidget.onReady(widgetOnReady(newWidget, true));

					newWidget.onClose(widgetOnClose);
				}
			} catch (ex) {
				console.warn('DevRev Plug initialize failed with error: ', ex); // eslint-disable-line
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [config, mode]);

	useEffect(() => {
		if (widget) {
			if (DEV_REV_DISABLED_ROUTES.includes(pathname)) {
				widget.close();
				setIsOpen(false);
			}
		}
	}, [pathname, widget]);

	const handleIconClick = React.useCallback(() => {
		try {
			if (widget) {
				if (isOpen) {
					widget.close();
				} else {
					widget.open();
				}
				setIsOpen(!isOpen);
			}
		} catch (ex) {
			console.warn('DevRev Plug open/close failed with error: ', ex); // eslint-disable-line
		}
	}, [widget, isOpen]);

	// close widget if exist once component unmounts
	useEffect(
		() => () => {
			if (widgetRef) {
				widgetRef.destroy();
			}
		},
		[]
	);

	useEffect(() => {
		if (shouldOpen) {
			handleIconClick();
			close();
		}
	}, [close, handleIconClick, shouldOpen]);

	return <DevRevButton onClick={handleIconClick} isLoading={isLoading} />;
};

export default DevRevWidget;
